import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SpaLink from './SpaLink';
import ProductsMenu from './ProductsMenu';
import { GROUPS_META, HOME_META, applyMeta } from './products';
import { GROUP_GAMES, COMPETITIONS, GAME_COUNT, GAMES_PLAN } from './gamesData';   // Oct 5, 2026 — the Games band

// GA4 tracking helper - safe to call even if gtag isn't loaded (e.g. on authenticated routes)
const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};

/**
 * GroupsPage — /groups, the Weekly Golf Groups product page (Nov 1, 2026).
 * Built from the pre-Nov-1 home page (LandingPage.js), which carried the group
 * plans and pricing; the home page is now the generic Tee Time Organizer page.
 * Plan: claude/Products_Menu_And_Home_Page_Plan_Nov01.md
 */
const GroupsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showContactModal, setShowContactModal] = useState(false);
  // `website` is the honeypot — see the hidden input in the contact modal and
  // the rules in backend/src/routes/contactRoutes.js. It rides inside
  // contactForm so it spreads into the POST body with everything else.
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '', organizationName: '', approximateSize: '', website: '' });
  // Stamped when the modal OPENS, not at page load: the landing page can sit
  // in a tab for hours before anyone clicks Contact Us, and a drop is silent.
  const [renderedAt, setRenderedAt] = useState(() => Date.now());
  const [inquiryType, setInquiryType] = useState('general'); // 'general' | 'club'
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Capture UTM parameters on landing for attribution through to signup.
  // Stored in sessionStorage (last-touch attribution, tab-scoped) so they
  // survive navigation to /register and make it into the registration POST
  // body. The RegisterPage reads these values back.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmData = {
      utm_source:   params.get('utm_source')   || null,
      utm_medium:   params.get('utm_medium')   || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_content:  params.get('utm_content')  || null,
      landing_page: window.location.pathname,
    };
    // Only overwrite sessionStorage if this visit actually has UTM data,
    // or if nothing's stored yet. This preserves attribution across
    // in-tab navigation.
    const hasAnyUtm = utmData.utm_source || utmData.utm_medium ||
                      utmData.utm_campaign || utmData.utm_content;
    const existing = sessionStorage.getItem('tto_attribution');
    if (hasAnyUtm || !existing) {
      sessionStorage.setItem('tto_attribution', JSON.stringify(utmData));
    }
  }, []);

  // Check for stored player token
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    if (params.get('landing') === 'true') return;
    if (params.get('from') === 'guide') return;
    
    const storedToken = localStorage.getItem('playerPortalToken');
    const tokenExpiry = localStorage.getItem('playerPortalTokenExpiry');
    
    if (storedToken && tokenExpiry) {
      const now = new Date().getTime();
      const expiry = parseInt(tokenExpiry, 10);
      
      if (now < expiry) {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone === true;
        
        if (isIOS || isPWA) {
          navigate(`/player?token=${storedToken}`);
          return;
        }
      } else {
        localStorage.removeItem('playerPortalToken');
        localStorage.removeItem('playerPortalTokenExpiry');
      }
    }
    
    if (params.get('contact') === 'true') {
      setShowContactModal(true);
    }
  }, [navigate, location]);

  // Per-route meta (Nov 1, 2026): index.html holds the home page's tags; this
  // page applies its own on mount and puts the home tags back on unmount. The
  // prerenderer captures the DOM after render, so /groups ships with these.
  useEffect(() => {
    applyMeta(GROUPS_META);
    return () => applyMeta(HOME_META);
  }, []);

  // Handle hash scrolling
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location.hash]);

  // Re-stamp every time the contact modal opens (see renderedAt above).
  useEffect(() => {
    if (showContactModal) setRenderedAt(Date.now());
  }, [showContactModal]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    setContactError('');
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...contactForm, inquiryType, formRenderedAt: renderedAt })
      });
      
      if (response.ok) {
        setContactSuccess(true);
        setContactForm({ name: '', email: '', message: '', organizationName: '', approximateSize: '', website: '' });
      } else {
        setContactError('Failed to send message. Please try again.');
      }
    } catch (err) {
      setContactError('Failed to send message. Please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  // Close-and-reset helper for the contact modal — keeps Club-state from leaking
  // into the next open (e.g. footer "Contact Us" after a Club submission).
  const closeContactModal = () => {
    setShowContactModal(false);
    setContactSuccess(false);
    setInquiryType('general');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Colors
  const colors = {
    green900: '#14532d', green800: '#166534', green700: '#15803d', green600: '#16a34a',
    green500: '#22c55e', green100: '#dcfce7', green50: '#f0fdf4',
    gray900: '#111827', gray800: '#1f2937', gray700: '#374151', gray600: '#4b5563',
    gray500: '#6b7280', gray400: '#9ca3af', gray300: '#d1d5db', gray200: '#e5e7eb',
    gray100: '#f3f4f6', gray50: '#f9fafb',
    amber500: '#f59e0b', amber400: '#fbbf24',
  };

  // Features data
  // Pricing tiers — Free + 4 paid (Club rendered separately below the grid)
  // NOTE: `id` is the canonical tier key RegisterPage reads from ?tier=X (free, starter,
  // plus, professional, league). Oct 5, 2026: these were still the pre-Phase-4 keys
  // (starter_new / starter / basic), so the Starter and Professional cards opened sign-up
  // on Free and the Plus card opened it on Starter. Fixed with the games-on-Plus change.
  //
  // `highlights` shows the upgrade narrative on each card. Free lists what's
  // included; paid tiers use "+ X" to read as "this tier adds X to the prior
  // tier." Professional and League differentiate on capacity (per the rollout
  // plan's persona: "same features as Plus, chosen for higher player capacity"),
  // so they describe capacity rather than listing "+ feature" items that aren't
  // actually new vs. Plus.
  const pricingTiers = [
    {
      id: 'free', name: 'Free', players: 'Up to 8 regular players',
      monthly: '$0', annual: null, savings: null,
      perPlayer: 'No credit card required', featured: false, ctaLabel: 'Get Started Free',
      highlights: [
        'Unlimited tee times',
        'Golf course library',
        'One-click player RSVP',
        'Tee sheet pairings',
        'Waitlist auto-promotion',
        'Player email messaging',
        'Weather forecasts',
      ],
    },
    {
      id: 'starter', name: 'Starter', players: 'Up to 20 regular players',
      monthly: '$4.99', annual: '$47.90', savings: '12',
      perPlayer: '~$0.25/player/month', featured: true, ctaLabel: 'Get Started',
      highlights: [
        'Everything in Free, plus:',
        'Advanced pairings & flights',
        'Mobile scoring',
        'Course handicaps',
      ],
    },
    {
      id: 'plus', name: 'Plus', players: 'Up to 40 regular players',
      monthly: '$9.99', annual: '$95.90', savings: '24',
      perPlayer: '~$0.25/player/month', featured: false, ctaLabel: 'Get Started',
      highlights: [
        'Everything in Starter, plus:',
        'Games: skins, Nassau, Sixes, Stableford & more',
        'SMS text reminders',
        'GHIN integration',
      ],
    },
    {
      id: 'professional', name: 'Professional', players: 'Up to 100 regular players',
      monthly: '$14.99', annual: '$143.90', savings: '36',
      perPlayer: '~$0.15/player/month', featured: false, ctaLabel: 'Get Started',
      highlights: [
        'Everything in Plus, games included',
        'Up to 100 regular players',
        'Built for active leagues',
      ],
    },
    {
      id: 'league', name: 'League', players: 'Up to 250 regular players',
      monthly: '$24.99', annual: '$239.90', savings: '60',
      perPlayer: '~$0.10/player/month', featured: false, ctaLabel: 'Get Started',
      highlights: [
        'Everything in Plus, games included',
        'Up to 250 regular players',
        'Built for formal leagues',
      ],
    },
  ];

  // Universal-everywhere features — items that apply to ALL tiers including Free
  // but are NOT already shown on the Free card (to avoid card-to-list duplication).
  // The Free card now lists Free's primary capabilities directly; this block
  // provides complementary reassurance: paid tiers don't take anything away.
  const includedFeatures = [
    'Cancel anytime',
    'Tee sheet reports',
    'Player database',
    'Secure & private',
  ];

  return (
    <div style={{ fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}>

      {/* Navigation */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(255, 255, 255, 0.97)', backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${colors.gray200}`, padding: isMobile ? '10px 16px' : '12px 32px',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SpaLink to="/" style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '14px', textDecoration: 'none' }}>
            <div style={{
              width: isMobile ? '40px' : '52px', height: isMobile ? '40px' : '52px', background: '#fff', borderRadius: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', border: `1px solid ${colors.gray200}`, overflow: 'hidden',
            }}>
              <img src="/logo.png" alt="Tee Time Organizer Logo" width="44" height="44" style={{ width: isMobile ? '32px' : '44px', height: isMobile ? '32px' : '44px', objectFit: 'contain' }} />
            </div>
            <div>
              <div style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: 800, color: colors.gray900, letterSpacing: '-0.5px' }}>Tee Time Organizer</div>
              {!isMobile && <div style={{ fontSize: '12px', color: colors.gray500, fontWeight: 500 }}>Weekly Golf Groups</div>}
            </div>
          </SpaLink>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {!isMobile && (
              <>
                <ProductsMenu isMobile={false} location="groups_header" current="groups" />
                <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }} style={{ padding: '10px 18px', fontSize: '14px', fontWeight: 600, color: colors.gray700, cursor: 'pointer', borderRadius: '8px', textDecoration: 'none' }}>Features</a>
                <SpaLink to="/games" onClick={() => trackEvent('games_link_click', { location: 'header' })} style={{ padding: '10px 18px', fontSize: '14px', fontWeight: 600, color: colors.gray700, cursor: 'pointer', borderRadius: '8px', textDecoration: 'none' }}>Games</SpaLink>
                <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }} style={{ padding: '10px 18px', fontSize: '14px', fontWeight: 600, color: colors.gray700, cursor: 'pointer', borderRadius: '8px', textDecoration: 'none' }}>Pricing</a>
              </>
            )}
            <button onClick={() => { trackEvent('demo_cta_click', { location: 'header' }); scrollToSection('try-demo'); }} style={{
              padding: isMobile ? '8px 12px' : '12px 24px', fontSize: isMobile ? '12px' : '14px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer',
              background: `linear-gradient(135deg, ${colors.amber400} 0%, ${colors.amber500} 100%)`, color: '#78350f',
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
            }}>{isMobile ? 'Demo' : '▶ View Demo'}</button>
            <button onClick={() => navigate('/login')} style={{
              padding: isMobile ? '8px 12px' : '12px 24px', fontSize: isMobile ? '12px' : '14px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer',
              background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`, color: 'white',
              boxShadow: '0 2px 8px rgba(22, 101, 52, 0.3)',
            }}>{isMobile ? 'Login' : 'Organizer Login'}</button>
          </div>
        </div>
        {/* Phone link row (Sept 25, 2026; Products menu Nov 1, 2026). The desktop
            header's links, always visible under the logo row; Products opens with a tap. */}
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '2px', marginTop: '6px' }}>
            <ProductsMenu isMobile location="groups_header_mobile" current="groups" />
            <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }} style={{ padding: '10px 7px', fontSize: '14px', fontWeight: 600, color: colors.gray700, cursor: 'pointer', borderRadius: '8px', textDecoration: 'none', whiteSpace: 'nowrap' }}>Features</a>
            <SpaLink to="/games" onClick={() => trackEvent('games_link_click', { location: 'header_mobile' })} style={{ padding: '10px 7px', fontSize: '14px', fontWeight: 600, color: colors.gray700, cursor: 'pointer', borderRadius: '8px', textDecoration: 'none', whiteSpace: 'nowrap' }}>Games</SpaLink>
            <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }} style={{ padding: '10px 7px', fontSize: '14px', fontWeight: 600, color: colors.gray700, cursor: 'pointer', borderRadius: '8px', textDecoration: 'none', whiteSpace: 'nowrap' }}>Pricing</a>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section style={{ padding: isMobile ? '134px 20px 40px' : '110px 32px 60px', background: 'white', position: 'relative', overflow: 'hidden' }}>
        <div style={{ 
          maxWidth: '1200px', margin: '0 auto', 
          display: isMobile ? 'block' : 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1.1fr', 
          gap: '60px', alignItems: 'center', position: 'relative' 
        }}>
          <div style={{ position: 'relative', zIndex: 2 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px', background: colors.green100, color: colors.green800,
              padding: '8px 16px', borderRadius: '50px', fontSize: '13px', fontWeight: 600, marginBottom: '24px',
              border: '1px solid rgba(22, 101, 52, 0.1)',
            }}>⭐ Simple. Fast. Reliable.</div>
            
            <h1 style={{
              fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? '36px' : '50px', fontWeight: 400, color: colors.gray900,
              lineHeight: 1.1, marginBottom: '24px', letterSpacing: '-1px',
            }}>
              Organize your <span style={{ color: colors.green700 }}>weekly golf group</span>
            </h1>
            
            <p style={{ fontSize: isMobile ? '15px' : '17px', color: colors.gray600, lineHeight: 1.7, marginBottom: '32px', maxWidth: '500px' }}>
              The golf group organizer for your regular game, whether you play three times a week or twice a month. Set up tee times, send invites by email or text, one-click RSVPs, promote waitlists, generate pairings, then play the games, score live and post to GHIN.{' '}
              <span style={{ color: colors.green700, fontWeight: 600 }}>Tee times in 30 seconds. Player invites in one tap. No more group texts, no more spreadsheets.</span>
            </p>
            
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <button onClick={() => { trackEvent('signup_cta_click', { location: 'hero', tier: 'free' }); navigate('/register?tier=free'); }} style={{
                padding: '12px 24px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer',
                background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`, color: 'white',
                boxShadow: '0 2px 8px rgba(22, 101, 52, 0.3)', display: 'inline-flex', alignItems: 'center', gap: '8px',
              }}>Get Started Free</button>
              <button onClick={() => { trackEvent('demo_cta_click', { location: 'hero' }); scrollToSection('try-demo'); }} style={{
                padding: '12px 24px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer',
                background: `linear-gradient(135deg, ${colors.amber400} 0%, ${colors.amber500} 100%)`, color: '#78350f',
                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
              }}>▶ View Demo</button>
            </div>
            <p style={{ fontSize: '12px', color: colors.gray400, marginTop: '12px', marginBottom: '0' }}>
              Free for up to 8 players · No credit card required · Upgrade anytime
            </p>
          </div>
          
          {/* Device Mockups - Hidden on mobile */}
          {!isMobile && (
            <div style={{ position: 'relative', zIndex: 1, height: '480px' }}>
              {/* Laptop */}
              <div style={{
                position: 'absolute', top: 0, left: 0, width: '520px', background: '#1a1a1a', borderRadius: '12px', padding: '6px',
                boxShadow: '0 30px 60px rgba(0, 0, 0, 0.35)', transform: 'perspective(1500px) rotateY(5deg) rotateX(2deg)',
              }}>
                <div style={{ background: '#f8f9fa', height: '320px', overflow: 'hidden', borderRadius: '6px' }}>
                  <img src="/organizer-screenshot.webp" alt="Tee Time Organizer dashboard - golf group management software showing tee time scheduling, player RSVPs, and weather forecasts" loading="eager" fetchpriority="high" width="520" height="320" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left' }} />
                </div>
                <div style={{ background: '#1a1a1a', height: '6px', borderRadius: '0 0 8px 8px' }}></div>
              </div>
              
              {/* Phone */}
              <div style={{
                position: 'absolute', top: '40px', right: 0, width: '200px', height: '420px', background: '#1a1a1a',
                borderRadius: '36px', padding: '12px', boxShadow: '0 30px 60px rgba(0, 0, 0, 0.4)',
                transform: 'perspective(1500px) rotateY(-8deg) rotateX(2deg)', zIndex: 10,
              }}>
                <div style={{
                  position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)',
                  width: '70px', height: '24px', background: '#1a1a1a', borderRadius: '0 0 16px 16px', zIndex: 2,
                }}></div>
                <div style={{ width: '100%', height: '100%', background: '#f8f9fa', borderRadius: '26px', overflow: 'hidden' }}>
                  <img src="/player-portal-screenshot.webp" alt="Player Portal mobile view showing golf RSVP interface with one-click confirmation" loading="lazy" width="176" height="396" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Trust Bar */}
      <section style={{ padding: isMobile ? '20px 16px' : '28px 32px', background: colors.gray50, borderTop: `1px solid ${colors.gray200}`, borderBottom: `1px solid ${colors.gray200}` }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: isMobile ? '24px' : '48px', flexWrap: 'wrap' }}>
          {[
            { icon: '🔒', text: 'Private & Secure' },
            { icon: '⚡', text: 'Setup in 5 minutes' },
            { icon: '📱', text: 'Works on any device' },
            { icon: '⛳', text: 'GHIN Integration' },
            { icon: '📧', text: 'Email & text reminders' },
            { icon: '⏱️', text: 'Automated waitlists' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: colors.gray600, fontSize: isMobile ? '12px' : '14px', fontWeight: 500 }}>
              <div style={{
                width: isMobile ? '32px' : '40px', height: isMobile ? '32px' : '40px', background: 'white', borderRadius: '10px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isMobile ? '14px' : '18px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', border: `1px solid ${colors.gray200}`,
              }}>{item.icon}</div>
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Event Management band (Sept 24, 2026 — Dennis: "right now nobody knows that we
          can handle events"). Sits right under the trust bar so a phone visitor meets it
          on the first scroll (the header link is desktop-only — no room beside Demo and
          Login on a phone). Copy lists only formats that exist today. */}
      <section style={{ padding: isMobile ? '32px 20px' : '44px 32px', background: `linear-gradient(135deg, ${colors.green900} 0%, ${colors.green800} 100%)`, color: 'white' }}>
        <div style={{
          maxWidth: '1100px', margin: '0 auto',
          display: isMobile ? 'block' : 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.3fr 1fr', gap: '40px', alignItems: 'center',
        }}>
          <div>
            <div style={{
              display: 'inline-block', background: 'rgba(255,255,255,0.14)', color: colors.green100,
              padding: '6px 14px', borderRadius: '50px', fontSize: '12px', fontWeight: 700, letterSpacing: '0.4px', marginBottom: '16px',
            }}>ALSO FROM TEE TIME ORGANIZER · TOURNAMENTS &amp; OUTINGS</div>
            <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? '28px' : '36px', fontWeight: 400, lineHeight: 1.15, margin: '0 0 14px', letterSpacing: '-0.5px', color: 'white' }}>
              Running a tournament, outing or member‑guest?
            </h2>
            <p style={{ fontSize: isMobile ? '15px' : '16px', lineHeight: 1.7, color: colors.green100, margin: '0 0 22px', maxWidth: '560px' }}>
              Tee Time Organizer runs the whole day: shotgun pairings in minutes, printed scorecards and cart signs, scoring from players&rsquo; phones, and a live leaderboard on the clubhouse TV. Official results the moment the last card is in.
            </p>
            <SpaLink to="/events" onClick={() => trackEvent('events_link_click', { location: 'groups_band' })} style={{
              display: 'inline-block', padding: '12px 24px', fontSize: '15px', fontWeight: 700, borderRadius: '10px', textDecoration: 'none',
              background: `linear-gradient(135deg, ${colors.amber400} 0%, ${colors.amber500} 100%)`, color: '#78350f',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            }}>See Tournaments &amp; Outings &rarr;</SpaLink>
            <p style={{ fontSize: '12px', color: colors.green100, opacity: 0.85, margin: '12px 0 0' }}>
              One event a year? An Event Pass is a one-time purchase &mdash; no subscription.
            </p>
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: isMobile ? '26px 0 0' : 0, display: 'grid', gap: '10px' }}>
            {[
              'Captain’s choice, stroke play and best ball',
              'USGA handicap allowances built in',
              'Printed scorecards and cart signs',
              'A live leaderboard on every phone and TV',
              'We never touch your entry money',
            ].map((t) => (
              <li key={t} style={{
                display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)',
                borderRadius: '10px', padding: '10px 14px', fontSize: isMobile ? '14px' : '15px', fontWeight: 600, color: 'white',
              }}>
                <span aria-hidden="true" style={{ color: colors.amber400, fontWeight: 800 }}>✓</span>{t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Games band (Oct 5, 2026 — Dennis: "the landing page should be updated to reference
          games and have access to a page that looks like the fact sheet"). The names come
          from gamesData.js, the same list as /games and the PDF fact sheet. */}
      <section id="games" style={{ padding: isMobile ? '36px 20px' : '48px 32px', background: colors.gray50, borderBottom: `1px solid ${colors.gray200}` }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            display: 'inline-block', background: colors.green100, color: colors.green800,
            padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px',
          }}>{GAME_COUNT} games &middot; scored live &middot; settled for you</div>
          <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? '28px' : '36px', color: colors.gray900, margin: '0 0 10px', letterSpacing: '-0.5px' }}>
            Play the games your group already plays
          </h2>
          <p style={{ fontSize: isMobile ? '15px' : '17px', color: colors.gray500, lineHeight: 1.6, maxWidth: '680px', margin: '0 auto 22px' }}>
            Pick the games, set the wager, and score on your phones. Tee Time Organizer does the handicap math, keeps the games live as each score goes in, and works out who pays whom.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px', maxWidth: '860px', margin: '0 auto 24px' }}>
            {[...GROUP_GAMES, ...COMPETITIONS].map((g) => (
              <span key={g.name} style={{
                background: 'white', border: `1px solid ${colors.gray200}`, borderRadius: '50px', padding: '7px 14px',
                fontSize: isMobile ? '13px' : '14px', fontWeight: 600, color: colors.gray700,
              }}>
                {g.name}{g.isNew && <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 800, color: '#92400e', background: '#fef3c7', borderRadius: '4px', padding: '1px 5px' }}>NEW</span>}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px' }}>
            <SpaLink to="/games" onClick={() => trackEvent('games_link_click', { location: 'groups_band' })} style={{
              display: 'inline-block', padding: '12px 24px', fontSize: '15px', fontWeight: 700, borderRadius: '10px', textDecoration: 'none',
              background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`, color: 'white',
              boxShadow: '0 2px 8px rgba(22, 101, 52, 0.3)',
            }}>See all the games &rarr;</SpaLink>
            <a href="/downloads/TTO_Games.pdf" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('games_pdf_click', { location: 'groups_band' })} style={{
              display: 'inline-block', padding: '12px 22px', fontSize: '15px', fontWeight: 700, borderRadius: '10px', textDecoration: 'none',
              background: 'white', color: colors.green800, border: `2px solid ${colors.green700}`,
            }}>Download the fact sheet (PDF)</a>
          </div>
          <p style={{ fontSize: '14px', color: colors.gray500, margin: '16px 0 0' }}>
            {GAMES_PLAN}.{' '}
            <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }} style={{ color: colors.green700, fontWeight: 600 }}>See pricing</a>
          </p>
        </div>
      </section>

      {/* Built for Every Kind of Golf Group — moved up from end of page (Step 4) */}
      <section style={{ padding: isMobile ? '36px 20px 24px' : '44px 32px 32px', background: 'white' }}>
        <header style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 28px' }}>
          <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? '28px' : '34px', color: colors.gray900, marginBottom: '8px', letterSpacing: '-0.5px' }}>Built for every kind of golf group</h2>
        </header>
        <div style={{
          maxWidth: '1100px', margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: isMobile ? '12px' : '16px',
        }}>
          {[
            { icon: '🏌️', title: 'Buddy Groups', desc: 'Stop managing your regular game through group texts. One place for everything.' },
            { icon: '👥', title: 'Big Weekly Groups', desc: 'Dozens of players across several tee times, with waitlists that fill themselves.' },
            { icon: '🏆', title: 'Weekly Leagues', desc: 'Balanced flights, games across the field, scoring and handicaps.' },
            { icon: '⛳', title: 'Men’s, Women’s & Senior Groups', desc: 'The right tees for every player and course handicaps worked out for you.' },
          ].map((item, i) => (
            <div key={i} style={{
              background: 'white', border: `1px solid ${colors.gray200}`, borderRadius: '12px',
              padding: isMobile ? '16px' : '20px', textAlign: 'center',
            }}>
              <div style={{ fontSize: isMobile ? '24px' : '28px', marginBottom: '8px' }}>{item.icon}</div>
              <h3 style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: 700, color: colors.gray900, margin: '0 0 6px 0' }}>{item.title}</h3>
              <p style={{ fontSize: isMobile ? '12px' : '13px', color: colors.gray500, margin: 0, lineHeight: 1.5 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section — Compact checklist (Step 4 redesign) */}
      <section id="features" style={{ padding: isMobile ? '24px 20px 36px' : '32px 32px 44px', background: 'white' }}>
        <header style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 36px' }}>
          <div style={{
            display: 'inline-block', background: colors.green100, color: colors.green800,
            padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px',
          }}>Features</div>
          <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? '32px' : '42px', color: colors.gray900, marginBottom: '16px', letterSpacing: '-0.5px' }}>Everything Your Golf Group Needs</h2>
          <p style={{ fontSize: isMobile ? '15px' : '17px', color: colors.gray500, lineHeight: 1.6 }}>From tee time scheduling to mobile scoring, automated reminders to handicap tracking — we've got your group covered.</p>
        </header>

        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: isMobile ? '10px 16px' : '12px 32px' }}>
          {[
            'Unlimited tee times',
            'Search thousands of courses',
            'One-click player RSVP',
            'Email reminders',
            'SMS text reminders',
            'Weather forecasts',
            'Tee sheet pairings',
            'Advanced pairings & flights',
            'Mobile scoring',
            'Course handicaps',
            'GHIN integration & auto-sync',
            'Detailed player stats',
            'Full-field scorecard reports',
            'Scorecard photo import',
            'Group RSVP — hold spots for your foursome',
            'Pairings visible to players',
            'Auto-promoting waitlists',
            'Player database & groups',
            'Activity tracking',
            'Tee sheet exports',
            'Mobile-friendly — no download',
            'Secure & private',
          ].map((feature, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'flex-start', gap: '10px',
              fontSize: isMobile ? '14px' : '15px', color: colors.gray700, lineHeight: 1.4,
              padding: '4px 0',
            }}>
              <span style={{ color: colors.green600, fontWeight: 700, flexShrink: 0 }}>✓</span>
              <span>{feature}</span>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: isMobile ? '28px' : '36px' }}>
          <button
            onClick={() => scrollToSection('try-demo')}
            style={{
              display: 'inline-block', padding: isMobile ? '12px 24px' : '14px 32px',
              background: 'white', color: colors.green800,
              border: `2px solid ${colors.green700}`, borderRadius: '10px',
              fontSize: isMobile ? '14px' : '15px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            See it in action — try the live demo ↓
          </button>
          <div style={{ fontSize: '13px', color: colors.gray500, marginTop: '10px' }}>
            Or skip ahead and start your free account in 30 seconds.
          </div>
        </div>
      </section>

      {/* How It Works — Compressed horizontal strip (Step 4 redesign) */}
      <section style={{ padding: isMobile ? '36px 20px' : '44px 32px', background: colors.gray50 }}>
        <header style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto 32px' }}>
          <div style={{
            display: 'inline-block', background: colors.green100, color: colors.green800,
            padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px',
          }}>How It Works</div>
          <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? '28px' : '36px', color: colors.gray900, marginBottom: '12px', letterSpacing: '-0.5px' }}>Set up in 5 minutes</h2>
          <p style={{ fontSize: isMobile ? '14px' : '16px', color: colors.gray500, lineHeight: 1.6 }}>No spreadsheets. No group texts. No download.</p>
        </header>

        <div style={{
          maxWidth: '900px', margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 24px 1fr 24px 1fr',
          alignItems: 'center', gap: isMobile ? '12px' : '0',
        }}>
          {[
            { num: '1', title: 'One-Time Setup', desc: 'Add your courses and players once. Takes about 5 minutes.' },
            { num: '2', title: 'Create & Publish', desc: 'Set tee time details and publish in under 30 seconds.' },
            { num: '3', title: 'Players RSVP', desc: 'One-click email confirmation. No app, no login needed.' },
          ].flatMap((step, i, arr) => {
            const elements = [
              <div key={`step-${i}`} style={{
                textAlign: 'center', padding: isMobile ? '16px' : '12px',
                background: isMobile ? 'white' : 'transparent',
                borderRadius: isMobile ? '12px' : '0',
                border: isMobile ? `1px solid ${colors.gray200}` : 'none',
              }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: '40px', height: '40px',
                  background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`,
                  color: 'white', borderRadius: '50%', fontWeight: 700, fontSize: '16px',
                  marginBottom: '10px', boxShadow: '0 4px 12px rgba(22, 101, 52, 0.2)',
                }}>{step.num}</div>
                <h4 style={{ fontSize: isMobile ? '15px' : '16px', fontWeight: 700, color: colors.gray900, margin: '0 0 6px 0' }}>{step.title}</h4>
                <p style={{ fontSize: isMobile ? '13px' : '14px', color: colors.gray500, margin: 0, lineHeight: 1.5 }}>{step.desc}</p>
              </div>
            ];
            if (i < arr.length - 1 && !isMobile) {
              elements.push(
                <div key={`arrow-${i}`} style={{
                  color: colors.gray400, fontSize: '24px', textAlign: 'center', fontWeight: 300,
                }}>→</div>
              );
            }
            return elements;
          })}
        </div>
      </section>

      {/* Testimonials — 3-card grid with 5-star ratings (Step 4 redesign) */}
      <section style={{ padding: isMobile ? '36px 20px' : '44px 32px', background: 'white' }}>
        <header style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto 36px' }}>
          <div style={{
            display: 'inline-block', background: '#fef3c7', color: '#92400e',
            padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px',
          }}>★ Testimonials</div>
          <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? '30px' : '38px', color: colors.gray900, marginBottom: '16px', letterSpacing: '-0.5px' }}>Trusted by Golf Groups & Leagues</h2>
        </header>

        <div style={{
          maxWidth: '820px', margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: isMobile ? '14px' : '20px',
        }}>
          {[
            {
              initials: 'DK', name: 'Dennis Kirwin', location: 'Mount Pleasant, SC', role: 'Organizer',
              text: "The app is a life saver — the days of managing text threads are over. Players sign up weeks in advance, and I love being able to form teams by handicap so everyone can see the pairings before showing up at the course.",
            },
            {
              initials: 'ST', name: 'Sam T.', location: 'Loveland, OH', role: 'Player',
              text: "I can RSVP from my phone in 10 seconds. No more 'reply all' text chains. The calendar sync is clutch.",
              accent: 'amber',
            },
            {
              initials: 'TM', name: 'Todd M.', location: 'Charleston, SC', role: 'Organizer',
              text: "With 75+ golfers, collecting RSVPs, updating handicaps, and building teams used to take hours every week. Now it's about 15 minutes — and the members love managing their own RSVPs.",
            },
            {
              initials: 'DM', name: 'Dan Moylan', location: 'The Rick Kelly Memorial Golf League', role: 'Organizer',
              text: "I manage 100+ league members. Before TTO, players signed up by replying to my daily email and I built the tee sheet by hand. Now signup is automated and the 'Reply All' nightmare is gone. After 15+ years organizing, it's worth a test drive.",
              accent: 'amber',
            },
          ].map((t, i) => (
            <article key={i} style={{
              background: 'white', border: `1px solid ${colors.gray200}`, borderRadius: '14px',
              padding: isMobile ? '20px' : '22px', display: 'flex', flexDirection: 'column',
            }}>
              <div style={{ color: colors.amber400, fontSize: '16px', marginBottom: '10px', letterSpacing: '2px' }}>★★★★★</div>
              <p style={{ fontSize: '14px', color: colors.gray700, lineHeight: 1.6, margin: '0 0 16px 0', fontStyle: 'italic', flexGrow: 1 }}>"{t.text}"</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: t.accent === 'amber'
                    ? `linear-gradient(135deg, ${colors.amber400}, ${colors.amber500})`
                    : `linear-gradient(135deg, ${colors.green700}, ${colors.green900})`,
                  color: t.accent === 'amber' ? '#78350f' : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '13px', flexShrink: 0,
                }}>{t.initials}</div>
                <div>
                  <div style={{ fontWeight: 700, color: colors.gray900, fontSize: '14px' }}>{t.name}</div>
                  <div style={{ fontSize: '12px', color: colors.gray500 }}>
                    {t.location}
                    {t.role && (
                      <span style={{
                        marginLeft: '8px', padding: '1px 7px', borderRadius: '10px',
                        background: colors.gray100, color: colors.gray600,
                        fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
                      }}>{t.role}</span>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" style={{ padding: isMobile ? '36px 20px' : '44px 32px', background: colors.gray50, scrollMarginTop: isMobile ? '120px' : '84px' }}>
        <header style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto 36px' }}>
          <div style={{
            display: 'inline-block', background: colors.green100, color: colors.green800,
            padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px',
          }}>Simple Pricing</div>
          <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? '32px' : '42px', color: colors.gray900, marginBottom: '16px', letterSpacing: '-0.5px' }}>Plans That Grow With Your Group</h2>
          <p style={{ fontSize: isMobile ? '15px' : '18px', color: colors.gray500, lineHeight: 1.6 }}>
            <strong style={{ color: colors.gray900 }}>Start free for up to 8 players.</strong> Upgrade as your group grows — unlock pairings, scoring, SMS reminders, and more from $4.99/month.
          </p>
          <p style={{ fontSize: isMobile ? '14px' : '16px', color: colors.green700, fontWeight: 700, marginTop: '12px' }}>
            No Per-Player Fees — The Organizer's Plan Covers It All.
          </p>
        </header>

        <div style={{ maxWidth: '1100px', margin: '0 auto 24px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(5, 1fr)', gap: '16px' }}>
          {pricingTiers.map((tier, i) => (
            <article key={i} style={{
              background: 'white', borderRadius: '20px', padding: isMobile ? '24px' : '24px', position: 'relative',
              border: tier.featured ? `2px solid ${colors.green600}` : `1px solid ${colors.gray200}`,
              transform: tier.featured && !isMobile ? 'scale(1.05)' : 'none',
              boxShadow: tier.featured ? '0 12px 32px rgba(22, 101, 52, 0.12)' : 'none',
              zIndex: tier.featured ? 2 : 1,
            }}>
              {tier.featured && <div style={{
                position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
                background: `linear-gradient(135deg, ${colors.green600}, ${colors.green700})`,
                color: 'white', padding: '6px 16px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap',
              }}>Most Popular</div>}
              <h3 style={{ fontSize: '20px', fontWeight: 700, color: colors.green800, marginBottom: '4px' }}>{tier.name}</h3>
              <div style={{ fontSize: '13px', color: colors.gray500, marginBottom: '20px' }}>{tier.players}</div>
              <div style={{ marginBottom: '16px' }}>
                <span style={{ fontSize: '13px', color: colors.gray500 }}>Monthly: </span>
                <span style={{ fontSize: '28px', fontWeight: 800, color: colors.gray900 }}>{tier.monthly}</span>
                {tier.id !== 'free' && <span style={{ fontSize: '14px', color: colors.gray500 }}>/mo</span>}
              </div>
              {tier.annual && (
                <div style={{ background: colors.green50, padding: '12px 14px', borderRadius: '10px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                    <span style={{ fontSize: '13px', color: colors.gray600 }}>Annual: </span>
                    <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'baseline' }}>
                      <span style={{ fontSize: '17px', fontWeight: 700, color: colors.gray900 }}>{tier.annual}</span>
                      <span style={{ fontSize: '13px', color: colors.gray500, marginLeft: '2px' }}>/yr</span>
                    </span>
                    <span style={{ background: colors.green600, color: 'white', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, marginLeft: '4px' }}>Save ${tier.savings}</span>
                  </div>
                </div>
              )}
              <div style={{ fontSize: '12px', color: colors.gray400, marginBottom: '16px', textAlign: 'center' }}>{tier.perPlayer}</div>
              {tier.highlights && tier.highlights.length > 0 && (
                <ul style={{
                  listStyle: 'none', padding: 0, margin: '0 0 20px 0',
                  fontSize: '13px', color: colors.gray700, lineHeight: 1.5,
                }}>
                  {tier.highlights.map((h, hi) => (
                    <li key={hi} style={{
                      display: 'flex', alignItems: 'flex-start', gap: '6px',
                      marginBottom: '6px',
                    }}>
                      <span style={{ color: colors.green600, fontWeight: 700, flexShrink: 0 }}>✓</span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div style={{ textAlign: 'center' }}>
                <button onClick={() => { trackEvent('signup_cta_click', { location: 'pricing', tier: tier.name }); navigate(`/register?tier=${tier.id}`); }} style={{
                  width: '100%', padding: '12px 24px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', cursor: 'pointer',
                  background: tier.featured ? `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)` : 'white',
                  color: tier.featured ? 'white' : colors.green800,
                  border: tier.featured ? 'none' : `2px solid ${colors.green700}`,
                  boxShadow: tier.featured ? '0 2px 8px rgba(22, 101, 52, 0.3)' : 'none',
                }}>{tier.ctaLabel}</button>
              </div>
            </article>
          ))}
        </div>

        {/* Club card — bespoke / contact-us */}
        <div style={{ maxWidth: '1100px', margin: '0 auto 32px' }}>
          <article style={{
            background: 'white', borderRadius: '20px', padding: isMobile ? '24px' : '28px',
            border: `1px solid ${colors.gray200}`,
            display: 'flex', flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '16px' : '32px',
          }}>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '20px', fontWeight: 800, color: colors.gray900, marginBottom: '4px' }}>Club</h3>
              <div style={{ fontSize: '13px', color: colors.gray500, marginBottom: '12px' }}>250+ players · Custom pricing</div>
              <p style={{ fontSize: '14px', color: colors.gray600, lineHeight: 1.6, margin: 0 }}>
                For pro shops, course operators, and institutional buyers. We'll work with you on pricing, billing terms, and onboarding.
              </p>
            </div>
            <div style={{ flexShrink: 0 }}>
              <button onClick={() => { setInquiryType('club'); setShowContactModal(true); }} style={{
                padding: '12px 32px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', cursor: 'pointer',
                background: 'white', color: colors.green800, border: `2px solid ${colors.green700}`,
              }}>Contact us</button>
            </div>
          </article>
        </div>

        {/* Features Included */}
        <div style={{ maxWidth: '900px', margin: '0 auto', background: 'white', borderRadius: '20px', padding: isMobile ? '20px' : '28px', border: `1px solid ${colors.gray200}` }}>
          <div style={{ textAlign: 'center', fontSize: '16px', fontWeight: 700, color: colors.gray700, marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Included on Every Plan</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '10px 24px' }}>
            {includedFeatures.map((feature, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: isMobile ? '13px' : '14px', color: colors.gray600 }}>
                <span style={{ color: colors.green600, fontWeight: 700 }}>✓</span> {feature}
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '20px', fontSize: isMobile ? '14px' : '15px', color: colors.green700, fontWeight: 600, fontStyle: 'italic' }}>
            ⚡ Create & publish tee times to your players in under 30 seconds
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="try-demo" style={{ padding: isMobile ? '32px 20px' : '40px 32px', background: 'white', textAlign: 'center' }}>
        <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? '28px' : '36px', color: colors.gray900, marginBottom: '16px' }}>Try the Live Demo</h2>
        <p style={{ fontSize: isMobile ? '14px' : '16px', color: colors.gray600, marginBottom: '28px' }}>See how easy it is to manage your golf group. Full access, no signup required.</p>
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => { trackEvent('demo_viewed', { type: 'organizer_dashboard' }); window.location.href = '/dashboard?demo=true&clear=auth'; }} style={{
            padding: isMobile ? '12px 20px' : '14px 32px', fontSize: isMobile ? '14px' : '15px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer',
            background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`, color: 'white',
            boxShadow: '0 2px 8px rgba(22, 101, 52, 0.3)',
          }}>🎯 View Organizer Dashboard</button>
          <button onClick={() => { trackEvent('demo_viewed', { type: 'player_portal' }); window.open('/p/s3DhoI', '_blank'); }} style={{
            padding: isMobile ? '12px 20px' : '14px 32px', fontSize: isMobile ? '14px' : '15px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer',
            background: `linear-gradient(135deg, ${colors.amber400} 0%, ${colors.amber500} 100%)`, color: '#78350f',
            boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
          }}>📱 View Player Portal</button>
        </div>
      </section>

      {/* CTA Section — Beefed up closing (Step 4 redesign) */}
      <section style={{
        padding: isMobile ? '56px 20px' : '72px 32px',
        background: `linear-gradient(135deg, ${colors.green800} 0%, ${colors.green900} 100%)`,
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? '32px' : '44px', color: 'white', marginBottom: '16px', letterSpacing: '-0.5px' }}>Ready to organize your golf group?</h2>
          <p style={{ fontSize: isMobile ? '15px' : '17px', color: 'rgba(255, 255, 255, 0.92)', lineHeight: 1.6, marginBottom: '12px' }}>
            Stop chasing RSVPs through text chains. Stop maintaining a spreadsheet. Get your group running on Tee Time Organizer in 5 minutes — free for up to 8 players, forever.
          </p>
          <p style={{ fontSize: isMobile ? '13px' : '14px', color: 'rgba(255, 255, 255, 0.78)', marginBottom: '32px' }}>
            No credit card required. Upgrade only if your group grows.
          </p>

          {/* Dual CTAs — signup primary + demo secondary */}
          <div style={{
            display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap',
            marginBottom: '36px',
          }}>
            <button
              onClick={() => { trackEvent('signup_cta_click', { location: 'cta_bottom', tier: 'free' }); navigate('/register?tier=free'); }}
              style={{
                padding: isMobile ? '14px 28px' : '16px 36px',
                background: 'white', color: colors.green900,
                borderRadius: '12px', fontSize: isMobile ? '15px' : '16px', fontWeight: 700,
                border: 'none', cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                display: 'inline-flex', alignItems: 'center', gap: '8px',
              }}
            >
              Start Organizing Today <span>⛳</span>
            </button>
            <button
              onClick={() => scrollToSection('try-demo')}
              style={{
                padding: isMobile ? '14px 28px' : '16px 36px',
                background: 'transparent', color: 'white',
                borderRadius: '12px', fontSize: isMobile ? '15px' : '16px', fontWeight: 600,
                border: '2px solid rgba(255, 255, 255, 0.5)', cursor: 'pointer',
              }}
            >
              ▶ See the demo first
            </button>
          </div>

          {/* Closing testimonial */}
          <div style={{
            maxWidth: '520px', margin: '0 auto',
            paddingTop: '28px', borderTop: '1px solid rgba(255, 255, 255, 0.15)',
            fontStyle: 'italic', fontSize: isMobile ? '14px' : '15px',
            color: 'rgba(255, 255, 255, 0.92)', lineHeight: 1.6,
          }}>
            <div style={{ color: colors.amber400, fontSize: '14px', marginBottom: '8px', letterSpacing: '2px' }}>★★★★★</div>
            "Super simple. I appreciate the weather in the reminders — nice to know what conditions we're facing before I leave the house."
            <div style={{ fontSize: '13px', opacity: 0.8, fontStyle: 'normal', marginTop: '8px' }}>
              — Kirk W., Isle of Palms, SC
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - SEO keyword targeting */}
      <section id="faq" style={{ padding: isMobile ? '32px 20px' : '40px 32px', background: 'white' }}>
        <header style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto 24px' }}>
          <div style={{
            display: 'inline-block', background: colors.green100, color: colors.green800,
            padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px',
          }}>FAQ</div>
          <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? '28px' : '38px', color: colors.gray900, marginBottom: '16px', letterSpacing: '-0.5px' }}>Frequently Asked Questions</h2>
        </header>
        
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {[
            { q: 'How do I organize my golf group with Tee Time Organizer?', a: 'Add your courses and players during a one-time setup (about 5 minutes). Then create a tee time, choose the course, date, time, and number of slots, and publish it with one click. Your players receive an email with a personal link to RSVP. The whole process takes under 30 seconds.' },
            { q: 'Do my players need to download an app?', a: 'No. Players access their portal through a web link — no app download, no account creation, no passwords. It works on any device with a browser. Players can optionally add it to their home screen for an app-like experience.' },
            { q: 'How do golf pairings and balanced flights work?', a: 'Tee Time Organizer generates pairings based on course handicaps. Choose balanced flights (A/B/C/D tiers evenly distributed across groups), randomized pairings, or manually arrange players. You can also set up shotgun starts with starting hole assignments. Print or email the tee sheet to your pro shop.' },
            { q: 'Can I track handicaps for my golf group?', a: 'Yes. Enter each player\'s GHIN handicap index, and Tee Time Organizer automatically calculates course handicaps using the official WHS formula based on the course rating, slope, and par of the tees being played.' },
            { q: 'How do the automated reminders work?', a: 'Organizers set their own reminder schedule. You can enable a day-before reminder, a same-day reminder, and an optional custom reminder (any number of days ahead), and choose exactly what time of day each one goes out. Default times are 6 PM the day before and 9 AM the morning of, but you can change either to whatever works for your group. All reminders include the course, time, weather forecast, and a link to the player\'s portal. Reminders are sent via email and SMS based on each player\'s communication preferences. Configure in Settings → Notifications.' },
            { q: 'Is Tee Time Organizer good for golf leagues?', a: 'It\'s built for exactly this. Whether you run a weekly men\'s or women\'s golf league, a senior group, or a corporate outing series, you get a player database, automated scheduling, RSVP tracking, pairings, handicap management, and automated communications — all in one platform.' },
            { q: 'How much does it cost?', a: 'Free for up to 8 players, no credit card required. Paid plans start at $4.99/month (Starter, 20 players), with Plus ($9.99, 40 players), Professional ($14.99, 100 players), and League ($24.99, 250 players). Annual billing saves up to 20%. Need more than 250 players? Contact us about our Club tier. Player counts are your regular players. Occasional guests are always free; a guest who plays often enough to be a regular in practice counts like one. Games (skins, Nassau, Sixes, Stableford and more) come with Plus and above.' },
            { q: 'What\'s included in the free plan?', a: 'The free plan supports up to 8 regular players (occasional guests are always free) and includes unlimited tee times, a searchable database of thousands of golf courses, one-click player RSVP, tee sheet pairings, auto-promoting waitlists, email messaging to players, and weather forecasts on every tee time. No credit card is required to sign up, and there is no time limit on the free tier.' },
            { q: 'Can I cancel my subscription anytime?', a: 'Yes. You can cancel your paid plan at any time from Settings. There are no long-term contracts, no cancellation fees, and no questions asked. If you cancel mid-billing-cycle, you keep access through the end of the period you already paid for, then automatically move to the free tier (which keeps your data intact for up to 8 players).' },
            { q: 'How does Tee Time Organizer compare to spreadsheets or group texts?', a: 'Spreadsheets and group texts require the organizer to manually chase RSVPs, copy player info between locations, calculate handicaps by hand, and send reminders one at a time. Tee Time Organizer automates all of that: players RSVP in one click, the system auto-promotes the waitlist when spots open, handicaps update automatically via GHIN, and reminders send themselves with weather forecasts. Most organizers save 2-4 hours per week of coordination work, and players stop missing tee times because of buried text messages.' },
            { q: 'Does it work for women\'s golf leagues, senior groups, and mixed groups?', a: 'Yes. Tee Time Organizer is designed for any kind of recurring golf group, including women\'s leagues, men\'s leagues, senior groups, mixed groups, junior programs, and corporate outings. You can configure different tees per player (for handicap calculations), set up flexible pairings (foursomes, threesomes, twosomes, A/B/C/D balanced flights), and customize messaging templates for your specific group.' },
          ].map((faq, i) => (
            <details key={i} style={{
              background: colors.gray50, border: `1px solid ${colors.gray200}`, borderRadius: '10px',
              padding: '0', marginBottom: '8px', cursor: 'pointer',
            }}>
              <summary style={{
                padding: isMobile ? '12px 16px' : '13px 20px', fontSize: isMobile ? '14px' : '15px', fontWeight: 600, color: colors.gray900,
                listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span>{faq.q}</span>
                <span style={{ color: colors.gray400, fontSize: '16px', marginLeft: '12px', flexShrink: 0 }}>+</span>
              </summary>
              <div style={{ padding: isMobile ? '0 16px 14px' : '0 20px 14px', fontSize: isMobile ? '14px' : '14px', color: colors.gray600, lineHeight: 1.6 }}>
                {faq.a}
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: isMobile ? '36px 20px 24px' : '52px 32px 28px', background: colors.gray900 }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

          {/* Footer top section: 4-column grid (brand + product + company + legal) */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr',
            gap: isMobile ? '28px' : '40px',
            marginBottom: '32px',
            paddingBottom: '28px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}>

            {/* Brand column (wider) */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                <div style={{
                  width: '40px', height: '40px', background: 'white', borderRadius: '10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                }}>
                  <img src="/logo.png" alt="Tee Time Organizer Logo" loading="lazy" width="32" height="32" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                </div>
                <div style={{ fontSize: '17px', fontWeight: 700, color: 'white' }}>Tee Time Organizer</div>
              </div>
              <p style={{ fontSize: '13px', color: colors.gray400, lineHeight: 1.7, margin: 0, maxWidth: '380px' }}>
                Golf group management software for organizing tee times, managing RSVPs, generating pairings and tee sheets, and sending automated reminders with weather forecasts. Built for weekly golf groups and leagues of any size.
              </p>
            </div>

            {/* Product column */}
            <div>
              <div style={{
                fontSize: '12px', fontWeight: 700, color: 'white',
                textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '14px',
              }}>Product</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '10px' }}>
                  <SpaLink to="/" style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Home</SpaLink>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }} style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Features</a>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }} style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Pricing</a>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <a href="#try-demo" onClick={(e) => { e.preventDefault(); scrollToSection('try-demo'); }} style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Live Demo</a>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }} style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>FAQ</a>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <SpaLink to="/games" onClick={() => trackEvent('games_link_click', { location: 'footer' })} style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Games</SpaLink>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <SpaLink to="/events" onClick={() => trackEvent('events_link_click', { location: 'footer' })} style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Tournaments &amp; Outings</SpaLink>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <SpaLink to="/guides" style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Guides</SpaLink>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <SpaLink to="/compare" style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Compare</SpaLink>
                </li>
              </ul>
            </div>

            {/* Company column */}
            <div>
              <div style={{
                fontSize: '12px', fontWeight: 700, color: 'white',
                textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '14px',
              }}>Company</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '10px' }}>
                  <button type="button" onClick={() => setShowContactModal(true)} style={{ background: 'none', border: 'none', padding: 0, color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none', fontFamily: 'inherit' }}>Contact Us</button>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <SpaLink to="/login" style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Organizer Login</SpaLink>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <a
                    href="https://www.facebook.com/profile.php?id=61586992568016"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: colors.gray300, fontSize: '14px', textDecoration: 'none' }}
                  >
                    Facebook ↗
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal column */}
            <div>
              <div style={{
                fontSize: '12px', fontWeight: 700, color: 'white',
                textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '14px',
              }}>Legal</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '10px' }}>
                  <SpaLink to="/privacy" style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Privacy Policy</SpaLink>
                </li>
              </ul>
            </div>

          </div>

          {/* Footer bottom bar */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '8px' : '0',
          }}>
            <div style={{ fontSize: '12px', color: colors.gray400 }}>
              © 2025–2026 Tee Time Organizer. All rights reserved.
            </div>
            <div style={{ fontSize: '12px', color: colors.gray500 }}>
              Made for weekly golf groups and leagues.
            </div>
          </div>

        </div>
      </footer>

      {/* Contact Modal */}
      {showContactModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }} onClick={closeContactModal}>
          <div style={{ background: 'white', borderRadius: '16px', padding: isMobile ? '24px' : '32px', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            {contactSuccess ? (
              <div style={{ background: colors.green50, color: colors.green800, padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>✅</div>
                <div style={{ fontWeight: 700, marginBottom: '8px' }}>Message Sent!</div>
                <div style={{ fontSize: '14px' }}>We'll get back to you soon.</div>
                <button onClick={closeContactModal} style={{
                  marginTop: '20px', padding: '12px 24px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer',
                  background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`, color: 'white',
                }}>Close</button>
              </div>
            ) : (
              <>
                <h3 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px', color: colors.gray900 }}>
                  {inquiryType === 'club' ? 'Talk to us about Club' : 'Contact Us'}
                </h3>
                <p style={{ fontSize: '14px', color: colors.gray500, marginBottom: '24px' }}>
                  {inquiryType === 'club'
                    ? 'Tell us a bit about your organization and we\'ll be in touch about pricing and onboarding.'
                    : 'Have questions? We\'d love to hear from you.'}
                </p>
                
                {contactError && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>{contactError}</div>}
                
                <form onSubmit={handleContactSubmit}>
                  {/* Bot trap. Off-screen rather than display:none (some scripts
                      skip hidden inputs), out of the tab order and hidden from
                      screen readers, so no keyboard or assistive-tech user can
                      reach it. Anything here came from a machine. */}
                  <input
                    type="text"
                    name="website"
                    value={contactForm.website}
                    onChange={e => setContactForm({ ...contactForm, website: e.target.value })}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                  />
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: colors.gray700 }}>Name</label>
                    <input type="text" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: `1px solid ${colors.gray300}`, borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }} value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: colors.gray700 }}>Email</label>
                    <input type="email" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: `1px solid ${colors.gray300}`, borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }} value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} />
                  </div>
                  {inquiryType === 'club' && (
                    <>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: colors.gray700 }}>Organization / club name</label>
                        <input type="text" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: `1px solid ${colors.gray300}`, borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }} value={contactForm.organizationName} onChange={e => setContactForm({ ...contactForm, organizationName: e.target.value })} />
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: colors.gray700 }}>Approximate group size</label>
                        <select required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: `1px solid ${colors.gray300}`, borderRadius: '8px', outline: 'none', boxSizing: 'border-box', background: 'white' }} value={contactForm.approximateSize} onChange={e => setContactForm({ ...contactForm, approximateSize: e.target.value })}>
                          <option value="">Select a range…</option>
                          <option value="under_50">Under 50</option>
                          <option value="50_100">50–100</option>
                          <option value="100_250">100–250</option>
                          <option value="250_plus">250+</option>
                        </select>
                      </div>
                    </>
                  )}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: colors.gray700 }}>Message</label>
                    <textarea required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: `1px solid ${colors.gray300}`, borderRadius: '8px', outline: 'none', minHeight: '120px', resize: 'vertical', boxSizing: 'border-box' }} value={contactForm.message} onChange={e => setContactForm({ ...contactForm, message: e.target.value })} />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                    <button type="button" onClick={closeContactModal} style={{
                      flex: 1, padding: '12px 24px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', cursor: 'pointer',
                      background: 'white', color: colors.green800, border: `2px solid ${colors.green700}`,
                    }}>Cancel</button>
                    <button type="submit" disabled={contactSubmitting} style={{
                      flex: 1, padding: '12px 24px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer',
                      background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`, color: 'white',
                    }}>{contactSubmitting ? 'Sending...' : 'Send Message'}</button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupsPage;
